<template>
  <f7-page :page-content="true" name="mapaaprobacion" @page:beforein="entrar">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('mapaaprobacion.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button icon-f7="arrow_clockwise" :text="$t('mapaaprobacion.recargar')" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('mapaaprobacion.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <!-- Los contadores son también el filtro: pinchar en uno deja solo ese estado. -->
      <f7-block strong class="contadores no-margin-bottom">
        <span
          v-for="c in contadores"
          :key="c.clave"
          class="contador"
          :class="[c.clase, { 'contador-apagado': filtroEstado && filtroEstado != c.clave }]"
          @click="filtrarPor(c.clave)"
        >{{ c.texto }}: {{ c.valor }}</span>
        <span class="contador contador-total" @click="filtrarPor(null)">{{ $t('mapaaprobacion.total') }}: {{ mapa.total || 0 }}</span>
      </f7-block>

      <f7-list no-hairlines class="no-margin-vertical filtros">
        <f7-list-input
          type="text"
          :placeholder="$t('mapaaprobacion.buscar')"
          :value="busqueda"
          clear-button
          @input="busqueda = ($event.target.value || '')"
          @input:clear="busqueda = ''"
        >
          <f7-icon slot="media" f7="search" size="18"></f7-icon>
        </f7-list-input>
        <f7-list-item class="toggle-inactivos">
          <span slot="title">{{ $t('mapaaprobacion.incluirInactivos') }}</span>
          <f7-toggle slot="after" :checked="incluirInactivos" @toggle:change="cambiarInactivos"></f7-toggle>
        </f7-list-item>
      </f7-list>

      <!-- Asignación en bloque: marcar personas arriba y ponerles aprobador de una vez. -->
      <f7-card class="padding-vertical-half padding-right bloque">
        <f7-card-header>
          {{ $t('mapaaprobacion.asignacionBloque') }}
          <span class="marcadas">
            {{ $t('mapaaprobacion.marcadas') }}: {{ marcados.length }}
            <!-- El filtro no desmarca: quien marcó a alguien y luego filtró sigue teniéndolo
                 dentro, y tiene que verlo antes de asignar a ciegas. -->
            <span v-if="marcadosOcultos.length" class="ocultos">
              {{ $t('mapaaprobacion.marcadasOcultas', { n: marcadosOcultos.length }) }}
            </span>
          </span>
        </f7-card-header>
        <f7-card-content>
          <r-form ref="formBloque" v-slot="{ rform }" :initialData="bloque">
            <f7-row no-gap class="list list-form no-hairlines">
              <f7-col width="100" medium="40">
                <ul>
                  <r-autocomplete
                    outline
                    floating-label
                    :dbAdapter="Empleado"
                    :label="$t('mapaaprobacion.aprobador')"
                    :form="rform"
                    name="aprobador"
                    idProp="username"
                    labelProp="nombre"
                    prefixProp="username"
                    :filter="{ filter: { activo: true, rolesExcluidos: ['CLIENTE'] } }"
                  ></r-autocomplete>
                </ul>
              </f7-col>
              <f7-col width="100" medium="40">
                <ul>
                  <r-input
                    outline
                    floating-label
                    :label="$t('mapaaprobacion.motivo')"
                    :form="rform"
                    type="text"
                    name="motivo"
                  ></r-input>
                </ul>
              </f7-col>
              <f7-col width="100" medium="20" class="display-flex justify-content-center align-items-center">
                <f7-button
                  fill
                  :disabled="!marcados.length"
                  icon-f7="person_2"
                  :text="$t('mapaaprobacion.asignar')"
                  class="display-flex"
                  @click="asignar"
                ></f7-button>
              </f7-col>
            </f7-row>
          </r-form>
          <p class="pie">{{ $t('mapaaprobacion.pieBloque') }}</p>
        </f7-card-content>
      </f7-card>

      <f7-block strong class="tabla-wrapper">
        <table class="tabla-mapa">
          <thead>
            <tr>
              <th class="col-check">
                <input type="checkbox" :checked="todosMarcados" @change="marcarTodos($event)" />
              </th>
              <th>{{ $t('mapaaprobacion.persona') }}</th>
              <th class="col-centro">{{ $t('mapaaprobacion.gestiona') }}</th>
              <th>{{ $t('mapaaprobacion.leAprueba') }}</th>
              <th>{{ $t('mapaaprobacion.suplente') }}</th>
              <th>{{ $t('mapaaprobacion.apruebaA') }}</th>
              <th>{{ $t('mapaaprobacion.estado') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fila in filasFiltradas" :key="fila.username" :class="{ inactivo: fila.activo === false }">
              <td class="col-check">
                <input type="checkbox" :checked="estaMarcado(fila.username)" @change="alternar(fila.username)" />
              </td>
              <td>
                <a v-if="puedeAbrirFichaAhora" href="#" @click.prevent="abrirFicha(fila)">{{ fila.nombre || fila.username }}</a>
                <span v-else>{{ fila.nombre || fila.username }}</span>
                <span class="username">{{ fila.username }}</span>
                <span v-if="fila.usuarioPrincipal" class="vinculo">
                  {{ $t('vacaciones.mismaPersona') }} {{ fila.usuarioPrincipal }}
                </span>
              </td>
              <td class="col-centro">{{ fila.gestionaVacaciones ? $t('mapaaprobacion.si') : $t('mapaaprobacion.no') }}</td>
              <td :class="{ 'no-operativo': fila.aprobador && fila.aprobadorActivo === false }">
                {{ fila.aprobadorNombre || fila.aprobador || '—' }}
              </td>
              <!-- El suplente de baja también deja a la persona sin cobertura: se marca igual que
                   el titular para que se vea cuál de los dos hay que cambiar. -->
              <td :class="{ 'no-operativo': fila.aprobadorSuplente && fila.aprobadorSuplenteActivo === false }">
                {{ fila.aprobadorSuplenteNombre || fila.aprobadorSuplente || '—' }}
                <span v-if="fila.aprobadorSuplente && fila.aprobadorSuplenteActivo === false" class="marca-baja">
                  {{ $t('mapaaprobacion.deBaja') }}
                </span>
              </td>
              <td class="col-apruebaa">{{ fila.apruebaA && fila.apruebaA.length ? fila.apruebaA.join(', ') : '—' }}</td>
              <td>
                <span class="etiqueta-estado" :class="'estado-' + fila.estado">{{ $t('vacaciones.estados.' + fila.estado) }}</span>
                <span
                  v-for="aviso in fila.avisos"
                  :key="aviso"
                  class="etiqueta-aviso"
                  :title="fila.usuariosMismoDni.join(', ')"
                >{{ textoCodigo('mapaaprobacion.avisos', aviso) }}</span>
              </td>
            </tr>
            <tr v-if="!filasFiltradas.length">
              <td colspan="7" class="vacio">{{ $t('mapaaprobacion.sinResultados') }}</td>
            </tr>
          </tbody>
        </table>
      </f7-block>
    </template>
  </f7-page>
</template>
<style scoped>
.contadores .contador {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  margin-right: 8px;
  margin-bottom: 4px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
}
.contador-apagado { opacity: 0.35; }
.contador-verde { background-color: #4cd964; }
.contador-rojo { background-color: #d32f2f; }
.contador-ambar { background-color: #ff9500; }
.contador-gris { background-color: #8e8e93; }
.contador-azul { background-color: #22355b; }
.contador-total { background-color: #444; }
.toggle-inactivos .item-title { font-size: 13px; font-weight: 600; color: #555; }
.bloque .card-header { font-weight: bold; color: #22355b; }
.bloque .marcadas { font-weight: normal; font-size: 13px; color: #555; }
.bloque .marcadas .ocultos { color: #b26a00; margin-left: 4px; }
.bloque .pie { font-size: 12px; color: #8e8e93; margin: 0 16px 8px; }
.tabla-wrapper { overflow-x: auto; }
.tabla-mapa { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-mapa th,
.tabla-mapa td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-mapa th { background-color: #f4f5f7; font-weight: bold; }
.tabla-mapa .col-check { width: 28px; }
.tabla-mapa .col-centro { text-align: center; }
.tabla-mapa .col-apruebaa { max-width: 220px; word-break: break-word; }
.tabla-mapa tr.inactivo td { color: #9aa1a8; font-style: italic; }
.tabla-mapa .username { display: block; font-size: 11px; color: #8e8e93; }
.tabla-mapa .no-operativo { color: #ff9500; }
.tabla-mapa .marca-baja { display: block; font-size: 11px; font-weight: bold; color: #ff9500; }
.tabla-mapa .vinculo { display: block; font-size: 11px; color: #22355b; }
.tabla-mapa .vacio { text-align: center; color: #8e8e93; padding: 16px; }
.etiqueta-estado,
.etiqueta-aviso {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  background-color: #8e8e93;
  margin-right: 4px;
}
.etiqueta-aviso { background-color: #ff9500; }
.estado-CORRECTO { background-color: #4cd964; }
.estado-SIN_APROBADOR,
.estado-SE_APRUEBA_A_SI_MISMO { background-color: #d32f2f; }
.estado-APROBADOR_INACTIVO { background-color: #ff9500; }
.estado-CUSPIDE { background-color: #22355b; }
.estado-NO_GESTIONA_VACACIONES { background-color: #8e8e93; }
</style>
<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import Empleado from "./../../js/db/Empleado.js";
import RhVacaciones from "./../../js/db/RhVacaciones.js";
import textoCodigo from "./../../js/utils/textoCodigo.js";
import permisosDeLaRuta from "./../../js/utils/permisosDeLaRuta.js";
import PermisosEmpleados from "./../empleados/permisos.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

/**
 * Ajustes · Mapa de aprobación (#52). Enseña de un vistazo quién puede solicitar vacaciones y
 * quién no, y permite ponerle aprobador a varias personas a la vez. La plantilla entera llega en
 * una sola petición y el filtrado es en memoria: son ~117 usuarios.
 */
export default {
  components: {
    rForm,
    rInput,
    rAutocomplete
  },
  data: function() {
    return {
      Empleado,
      mapa: {},
      marcados: [],
      busqueda: '',
      filtroEstado: null,
      incluirInactivos: false,
      bloque: { aprobador: null, motivo: null },
      ultimaCarga: 0,
      error403: false
    };
  },
  // El mapa lo ven ADMIN y DIRECTOR, pero la ficha del empleado es de otro módulo y puede que el
  // menú del usuario no la incluya: entonces el nombre se pinta sin enlace en vez de llevar a un
  // 403 (revisión CODEX del PR #1707).
  //
  // [#65, 02/10/2026] Llega resuelto desde la barrera de la ruta, con el usuario que ha traído
  // `Usuario.me()` en esta carga. Antes lo resolvía la pantalla y lo revisaba con un temporizador
  // al aparecer el menú; el menú guardado en `localStorage` contestaba al instante con el de la
  // sesión anterior y la revisión terminaba antes de empezar (ver `barreraUsuarioActualizado.js`).
  //
  // [#70, 17/10/2026] Y el `prop` es el valor de arranque: lo que se pinta es
  // `puedeAbrirFichaAhora`, que se vuelve a preguntar en cada entrada. La ruta lleva `keepAlive`,
  // así que esta instancia sobrevive al cambio de cuenta y los `props` de la resolución siguiente
  // no llegan a ninguna parte (ver `permisosDeLaRuta`).
  mixins: [permisosDeLaRuta({ puedeAbrirFicha: PermisosEmpleados.abrirFicha })],
  watch: {
    // Lo marcado es siempre lo que se está viendo. Sin esto, marcar unas filas y cambiar después
    // el buscador o el filtro de estado dejaba esas personas marcadas pero fuera de pantalla:
    // «Asignar» seguía habilitado sin ninguna casilla visible marcada y les cambiaba el aprobador
    // a ciegas, que es justo lo que «Marcar todos» evita al limitarse a las filas mostradas
    // (revisión CODEX del PR #1707). El contador «Marcadas» enseña cómo baja al filtrar, así que
    // no se pierde nada en silencio.
    busqueda: function() { this.podarMarcados(); },
    filtroEstado: function() { this.podarMarcados(); }
  },
  computed: {
    contadores: function() {
      var m = this.mapa;
      return [
        { clave: 'CORRECTO', clase: 'contador-verde', texto: this.$t('vacaciones.estados.CORRECTO'), valor: m.correctos || 0 },
        { clave: 'SIN_APROBADOR', clase: 'contador-rojo', texto: this.$t('vacaciones.estados.SIN_APROBADOR'), valor: m.sinAprobador || 0 },
        { clave: 'SE_APRUEBA_A_SI_MISMO', clase: 'contador-rojo', texto: this.$t('vacaciones.estados.SE_APRUEBA_A_SI_MISMO'), valor: m.seApruebanASiMismos || 0 },
        { clave: 'APROBADOR_INACTIVO', clase: 'contador-ambar', texto: this.$t('vacaciones.estados.APROBADOR_INACTIVO'), valor: m.aprobadorInactivo || 0 },
        { clave: 'NO_GESTIONA_VACACIONES', clase: 'contador-gris', texto: this.$t('vacaciones.estados.NO_GESTIONA_VACACIONES'), valor: m.noGestionan || 0 },
        { clave: 'CUSPIDE', clase: 'contador-azul', texto: this.$t('vacaciones.estados.CUSPIDE'), valor: m.cuspides || 0 },
        { clave: 'USUARIOS_SIN_VINCULAR', clase: 'contador-ambar', texto: this.$t('mapaaprobacion.avisos.USUARIOS_SIN_VINCULAR'), valor: m.usuariosSinVincular || 0 }
      ];
    },
    filasFiltradas: function() {
      var self = this;
      var filas = self.mapa.filas || [];
      if (self.filtroEstado == 'USUARIOS_SIN_VINCULAR') {
        filas = filas.filter(function(f) {
          return (f.avisos || []).indexOf('USUARIOS_SIN_VINCULAR') !== -1;
        });
      } else if (self.filtroEstado) {
        filas = filas.filter(function(f) { return f.estado == self.filtroEstado; });
      }
      var q = self.normalizarTexto(self.busqueda);
      if (!q) return filas;
      return filas.filter(function(f) {
        return self.normalizarTexto((f.nombre || '') + ' ' + (f.username || '')).indexOf(q) !== -1;
      });
    },
    todosMarcados: function() {
      var self = this;
      var filas = self.filasFiltradas;
      return filas.length > 0 && filas.every(function(f) { return self.estaMarcado(f.username); });
    },
    /** Marcados que el filtro o la búsqueda han dejado fuera de la tabla. */
    marcadosOcultos: function() {
      const self = this;
      return self.marcados.filter(function(u) {
        return !self.filasFiltradas.some(function(f) { return f.username == u; });
      });
    }
  },
  methods: {
    /**
     * Entrar en la pantalla. No es `cargar` directamente porque con `keepAlive` la instancia es la
     * que montó la primera resolución de la ruta: el permiso del enlace a la ficha hay que volver
     * a preguntarlo aquí, que es donde ya ha resuelto la barrera y contesta con quien está delante
     * (ver `permisosDeLaRuta`). «Recargar» y la recarga de «Asignar» llaman solo a `cargar`.
     */
    entrar: function() {
      this.revisarPermisosDeLaRuta();
      this.cargar();
    },
    textoCodigo: function(espacio, codigo) {
      return textoCodigo(this, espacio, codigo);
    },
    normalizarTexto: function(t) {
      return (t || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    estaMarcado: function(username) {
      return this.marcados.indexOf(username) !== -1;
    },
    alternar: function(username) {
      var i = this.marcados.indexOf(username);
      if (i === -1) this.marcados.push(username);
      else this.marcados.splice(i, 1);
    },
    // Se queda marcado solo quien sigue a la vista. Se reasigna únicamente si algo cae, para no
    // repintar la tabla en cada tecla del buscador.
    podarMarcados: function() {
      var visibles = this.filasFiltradas.map(function(f) { return f.username; });
      var quedan = this.marcados.filter(function(u) { return visibles.indexOf(u) !== -1; });
      if (quedan.length !== this.marcados.length) this.marcados = quedan;
    },
    // Marca o desmarca lo que se está viendo, no la plantilla entera: con un filtro puesto,
    // marcar "todos" y que entraran los ocultos sería una asignación a ciegas.
    marcarTodos: function(evento) {
      var self = this;
      if (evento.target.checked) {
        self.filasFiltradas.forEach(function(f) {
          if (!self.estaMarcado(f.username)) self.marcados.push(f.username);
        });
      } else {
        self.marcados = self.marcados.filter(function(u) {
          return !self.filasFiltradas.some(function(f) { return f.username == u; });
        });
      }
    },
    filtrarPor: function(estado) {
      this.filtroEstado = this.filtroEstado == estado ? null : estado;
    },
    cambiarInactivos: function(valor) {
      this.incluirInactivos = valor;
      this.cargar();
    },
    // El mapa lista usuarios y la ficha se abre por id de empleado, así que el id lo trae ya
    // cada fila. [revisión #1711] Antes se resolvía con un listado de empleados filtrado por
    // username, que devolvía la ficha entera de esa persona —correo, teléfono, dni, firma— a un
    // DIRECTOR, cuyo permiso aquí es el de las vacaciones y no el de la ficha de personal.
    abrirFicha: function(fila) {
      if (!fila.idEmpleado) {
        this.$f7.dialog.alert(this.$t('mapaaprobacion.sinFicha'));
        return;
      }
      this.$f7router.navigate('/empleados/view/' + fila.idEmpleado + '/');
    },
    /**
     * Solo se pinta la respuesta de la última carga pedida: conmutar «Incluir personas de baja»
     * deja varias peticiones en vuelo y una antigua que llegara la última dejaría la tabla sin
     * corresponder con el interruptor, y con ella la selección del bloque (revisión CODEX del
     * PR #1707).
     */
    cargar: function() {
      var self = this;
      var app = self.$f7;
      var carga = ++self.ultimaCarga;
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que
      // la apagaba. Cada operación suelta solo su testigo y el modal se quita cuando no queda
      // ninguno (`esperaGlobal`), que es lo que hacía aquí la cuenta de `bloqueos` [revisión #1711]:
      // una carga a destiempo no pinta nada, pero suelta su espera, y una asignación que recarga
      // por dentro no se queda sin preloader porque la recarga haya terminado antes que ella.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      // Se devuelve la promesa para que quien recargue tras escribir (asignar) pueda esperarla.
      return RhVacaciones.mapa(self.incluirInactivos)
        .then(function(res) {
          if (carga !== self.ultimaCarga) return;
          self.mapa = res || {};
          // Se olvidan las marcas de quien ya no se ve: ni quien desapareció de la plantilla, ni
          // quien se haya quedado fuera del filtro con la tabla nueva.
          self.podarMarcados();
        })
        .catch(function(e) {
          if (carga !== self.ultimaCarga) return;
          if (self.es403(e)) self.error403 = true;
          else app.dialog.alert(e && e.message ? e.message : self.$t('mapaaprobacion.errorCargando'));
        })
        .then(function() {
          // El preloader lo quita la última operación que quede en curso, no la primera que
          // termine: si lo quitara una carga obsoleta, la pantalla parecería lista con la tabla
          // aún por llegar.
          espera.cerrar();
        });
    },
    /**
     * Asignar en bloque cambia el aprobador de todo lo marcado, y lo marcado no se pierde al
     * filtrar. Si parte de la selección no se está viendo se dice quiénes son y se pide
     * confirmación: desmarcarlos solo, sin avisar, sería igual de sorprendente que asignarlos.
     */
    asignar: function() {
      const self = this;
      const ocultos = self.marcadosOcultos;
      if (!ocultos.length) {
        self.enviarAsignacion();
        return;
      }
      self.$f7.dialog.confirm(
        self.$t('mapaaprobacion.confirmarOcultas', { n: ocultos.length, quienes: ocultos.join(', ') }),
        self.$t('mapaaprobacion.asignacionBloque'),
        function() { self.enviarAsignacion(); });
    },
    enviarAsignacion: function() {
      var self = this;
      var app = self.$f7;
      if (!self.marcados.length) return;
      var datos = self.$refs.formBloque ? self.$refs.formBloque.formData : {};
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhVacaciones.asignarAprobador(self.marcados.slice(), datos.aprobador || null, datos.motivo || null)
        .then(function(res) {
          res = res || {};
          var texto = self.$t('mapaaprobacion.resultado', {
            actualizados: res.actualizados || 0,
            sinCambios: res.sinCambios || 0
          });
          self.marcados = [];
          // Primero la recarga y después el resultado: el preloader sigue puesto hasta que
          // llega la tabla nueva —si se soltara al acabar el POST, quedaría en pantalla la
          // vieja y manejable, y se podría volver a marcar y asignar sobre estados que ya han
          // cambiado (revisión CODEX del PR #1707)— y el mensaje se lee sobre lo que ha
          // quedado, no sobre lo que había.
          return self.cargar().then(function() {
            if (res.errores && res.errores.length) {
              // Los rechazos se enseñan uno a uno: son la parte accionable del resultado.
              app.dialog.alert(texto + '<br><br>' + res.errores.join('<br>'),
                self.$t('mapaaprobacion.asignacionBloque'));
            } else {
              app.toast.create({ text: texto, position: 'center', closeTimeout: 2500 }).open();
            }
          });
        })
        .catch(function(e) {
          app.dialog.alert(e && e.message ? e.message : self.$t('mapaaprobacion.errorAsignando'));
        })
        .then(function() {
          espera.cerrar();
        });
    }
  }
};
</script>
